import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import React from "react";
import { container_classname, sub_section_heading } from "../constants";

interface PerformanceStatsProps {
    metrics: number;
    metricsSuffix: string;
    highlights: string;
}

interface UserReviewProps {
    feedback: string;
    name: string;
    occupation: string;
    performanceStats: PerformanceStatsProps[];
}

interface TestimonialSectionDataProps {
    clientName: string;
    userReview: UserReviewProps;
    imageUrl: string;
    logoUrl: string;
    mobileCompactLogo: string;
}

const defaultTestimonialSectionData: TestimonialSectionDataProps[] = [
    {
        clientName: "Zopreneurs - UAE",
        userReview: {
            feedback: "With Gallabox, we cut busywork and gave our team more time to sell. The ROI speaks for itself.",
            name: "Satyen Babla",
            occupation: "CEO & Founder, Zopreneurs - UAE ",

            performanceStats: [
                { metrics: 15, metricsSuffix: "K+", highlights: "leads generated in 6 months" },
                { metrics: 30, metricsSuffix: "%", highlights: "reduction in sales agent hours" },
                { metrics: 2500, metricsSuffix: "+", highlights: "bookings/ meetings via WhatsApp flows" },
            ],
        },
        imageUrl: "https://assets.gallabox.com/gb-home/partner-program/zop.png",
        logoUrl: "https://assets.gallabox.com/gb-home/partner-program/zop.png",
        mobileCompactLogo: "https://assets.gallabox.com/gb-home/partner-program/zop.png",
    },
    {
        clientName: "Bluetick - Cape Town, South Africa",
        userReview: {
            feedback: "Gallabox is the backbone of what we deliver to clients.",
            name: "Matt Brownell",
            occupation: "Co-Founder, Bluetick - Cape Town, South Africa",

            performanceStats: [
                { metrics: 250, metricsSuffix: "K+", highlights: "messages sent" },
                { metrics: 0.8, metricsSuffix: "%", highlights: "opt-out rate" },
                { metrics: 3, metricsSuffix: "X", highlights: "increase in sponsors" },
            ],
        },
        imageUrl: "https://assets.gallabox.com/gb-home/partner-program/bluetick.png",
        logoUrl: "https://assets.gallabox.com/gb-home/partner-program/bluetick.png",
        mobileCompactLogo: "https://assets.gallabox.com/gb-home/partner-program/bluetick.png",
    },
];

interface TestimonialSectionProps {
    testimonialSectionData?: TestimonialSectionDataProps[];
}

const PartnerSuccessStories: React.FC<TestimonialSectionProps> = (props) => {
    const { testimonialSectionData = defaultTestimonialSectionData } = props;
    const [opacities, setOpacities] = React.useState<number[]>([]);
    const [previousIndex, setPreviousIndex] = React.useState<number>(0);
    const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
    const [randomNumber, setRandomNumber] = React.useState<number>(Math.random());

    const autoPlay = true;
    const autoPlayInterval = 7000;

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slides: testimonialSectionData.length,
        loop: true,
        drag: false,
        detailsChanged(slider) {
            const newOpacities = slider.track.details.slides.map((slide) => slide.portion);
            newOpacities.forEach((opacity, idx) => {
                const elements = document.querySelectorAll(`[id="slider-id-${idx}"]`);
                elements.forEach((elm) => {
                    if (!elm) return;
                    (elm as HTMLElement).style.opacity = String(opacity);
                });
            });
            setOpacities(newOpacities);
        },
    });

    const currentSlideIndex = instanceRef.current?.track?.details?.rel;

    const [thumbnailRef] = useKeenSlider(
        {
            initial: 0,
            slides: {
                perView: 4,
                spacing: 10,
            },
        },
        [
            (slider) => {
                const addClickEvents = () => {
                    slider.slides.forEach((slide, idx) => {
                        slide.addEventListener("click", () => {
                            if (instanceRef.current) {
                                if (typeof instanceRef.current?.track?.details?.rel === "number") {
                                    setPreviousIndex(instanceRef.current?.track?.details?.rel);
                                }
                                instanceRef.current.moveToIdx(idx);
                                setSelectedIndex(idx);
                                setRandomNumber(Math.random());
                            }
                        });
                    });
                };

                slider.on("created", () => {
                    if (!instanceRef.current) return;
                    addClickEvents();
                    if (instanceRef.current) {
                        instanceRef.current.on("animationStarted", (main) => {
                            const nextIdx = main.animator.targetIdx || 0;
                            slider.moveToIdx(Math.min(slider.track.details?.maxIdx, nextIdx));
                            setRandomNumber(Math.random());
                        });
                    }
                });
            },
        ]
    );

    const desktopLogos = testimonialSectionData.map((testimony) => testimony.logoUrl);

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (autoPlay && instanceRef.current) {
                instanceRef.current.next();
                setSelectedIndex((prev) => {
                    if (prev === testimonialSectionData.length - 1) return 0;
                    return prev + 1;
                });
            }
        }, autoPlayInterval);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [randomNumber]);

    return (
        <section className="lg:py-20 pt-16 pb-5">
            <div className={`${container_classname} flex flex-col lg:gap-14 gap-10`}>
                <div>
                    <h2 className={`${sub_section_heading} mb-5`} style={{ fontFamily: "Degular Display" }}>
                        Partner success stories
                    </h2>
                </div>
                <div className="rounded-lg lg:p-10 p-3" style={{ background: "#3364FA1A" }}>
                    <div className="lg:block hidden">
                        <div ref={sliderRef} className="fader-partner">
                            <Testimony testimonialSectionData={testimonialSectionData} selectedIndex={selectedIndex} previousIndex={previousIndex} opacities={opacities} />
                        </div>
                    </div>
                    <div className="block lg:hidden">
                        <div ref={sliderRef} className="fader-partner-mobile">
                            <Testimony testimonialSectionData={testimonialSectionData} selectedIndex={selectedIndex} previousIndex={previousIndex} opacities={opacities} />
                        </div>
                    </div>

                    <div className="w-full lg:hidden block">
                        {/* <div className="w-full flex keen-slider thumbnail-slider" ref={thumbnailRef}>
                            {mobileLogos.map((logo, idx) => {
                                const customStyle = currentSlideIndex === idx ? "loading" : "inactive";
                                return (
                                    <div key={idx} className="w-1/4">
                                        <div className={`box ${customStyle}`}>
                                            <div className={`box-border-top ${customStyle}`}></div>
                                        </div>
                                        <div className="flex keen-slider__slide thumbnail-slider__slide h-11" style={{ background: "#F6F6F6" }}>
                                            <Image width={76} height={20} src={logo} alt="" className="p-2 flex-shrink-0" style={{ width: "100%", height: "100%" }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div> */}
                        <style jsx>
                            {`
                                .thumbnail-slider .thumbnail-slider__slide {
                                    max-width: unset !important;
                                    transform: none !important;
                                }
                            `}
                        </style>
                    </div>
                </div>
                <div className="lg:block hidden">
                    <div className="w-full keen-slider thumbnail-slider flex items-center justify-center gap-8" ref={thumbnailRef}>
                        {desktopLogos.map((logo, idx) => {
                            const customStyle = currentSlideIndex === idx ? "loading" : "inactive";
                            return (
                                <div key={idx} className="w-1/6">
                                    <div className={`box ${customStyle}`}>
                                        <div className={`box-border-top ${customStyle}`}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <style jsx>
                        {`
                            .thumbnail-slider .thumbnail-slider__slide {
                                max-width: unset !important;
                                transform: none !important;
                            }
                        `}
                    </style>
                </div>
            </div>
        </section>
    );
};

export default PartnerSuccessStories;

interface TestimonyProps {
    testimonialSectionData: TestimonialSectionDataProps[];
    selectedIndex: number;
    previousIndex: number;
    opacities: number[];
}

const Testimony: React.FC<TestimonyProps> = (props) => {
    const { testimonialSectionData, selectedIndex, previousIndex, opacities } = props;
    return (
        <div>
            {testimonialSectionData.map((testimony, idx) => {
                const { clientName, userReview, imageUrl, logoUrl } = testimony;
                const { feedback, name, occupation, performanceStats } = userReview;

                return (
                    <div
                        key={idx}
                        id={`slider-id-${idx}`}
                        className="fader__slide w-full pb-0"
                        style={{ opacity: selectedIndex === idx || previousIndex === idx ? opacities[idx] : 0 }}
                    >
                        <div className="lg:hidden block bg-white px-3 py-2 shadow-lg w-fit rounded-lg border border-solid border-gray-50">
                            <Image width={164} height={66} src={logoUrl} alt={`${clientName} logo`} />
                        </div>

                        <div className="lg:block hidden pb-4">
                            <Image width={31} height={22} src={"https://assets.gallabox.com/gb-home/new-landing/testimony/quote.png"} alt="quotation mark" />
                        </div>

                        <div className="w-full flex lg:gap-14 gap-0 lg:pt-0 pt-6">
                            <div className="w-full lg:flex-1 flex flex-col lg:gap-11 gap-8">
                                <div className="w-full flex flex-col gap-4">
                                    <p className="lg:text-xl lg:max-w-3xl text-base font-medium text-black">{feedback}</p>
                                    <div>
                                        <p className="text-base font-semibold text-gray-900">{name}</p>
                                        <p className="text-sm text-gray-700">{occupation}</p>
                                    </div>
                                </div>
                                <div className="w-full flex lg:gap-6 gap-4">
                                    {performanceStats.map((performance, index) => {
                                        const { metrics, metricsSuffix, highlights } = performance;
                                        return (
                                            <div key={index} className="lg:pl-3 pl-3 flex flex-col gap-2 h-auto lg:w-1/3 w-full" style={{ borderLeft: "3px solid #3364FA" }}>
                                                <p className="lg:text-6xl text-xl font-bold" style={{ fontFamily: "Degular Variable" }}>
                                                    {metrics}
                                                    <span className="lg:text-6xl text-xl font-bold" style={{ fontFamily: "Degular Variable" }}>
                                                        {metricsSuffix}
                                                    </span>
                                                </p>
                                                <p className="lg:text-base text-xs lg:w-full w-20">{highlights}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="lg:flex hidden pt-1" style={{ width: "360px", height: "364px" }}>
                                <Image width={446} height={372} src={imageUrl} alt={`${clientName} image`} />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
